import React, { useEffect, useRef, useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import styled from 'styled-components'
import storage, { StorageEnum } from 'utils/storage'
import { IUser } from 'features/auth/types'
import { formatClaimStatusLabel } from 'features/claims/claimsApi'
import { useClaimStatuses, useDetailedClaimStatuses } from 'features/claims/claimsHooks'
import { useAppealEnumDescriptions } from 'features/claims/hooks/useAppealEnumDescriptions'
import { formatAppealStatusDescription } from 'utils/dataExtractor'
import { userNameFilterParams } from '../ClaimsTableUtils'
import { DateRangeField } from './DateRangeField'
import { LastModifiedQuick } from './LastModifiedQuick'
import type { FilterFormState, LastModifiedQuickState } from './filterTypes'

/**
 * Props for FilterFields component
 */
type FilterFieldsProps = {
  form: FilterFormState
  lastModQuick: LastModifiedQuickState
  onUpdateField: <K extends keyof FilterFormState>(key: K, value: FilterFormState[K]) => void
  onLastModQuickChange: (state: LastModifiedQuickState) => void
}

/**
 * Inline MultiSelect Component with Search
 */
const MultiSelectWithSearch: React.FC<{
  label: string
  options: string[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  isLoading?: boolean
  highlightOption?: string
}> = ({ label, options, selectedValues, onChange, isLoading = false, highlightOption }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchText('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Focus the search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const toggleOption = (option: string) => {
    const trimmedOption = option.trim()
    const trimmedSelectedValues = selectedValues.map((v) => v.trim())

    if (trimmedSelectedValues.includes(trimmedOption)) {
      onChange(selectedValues.filter((v) => v.trim() !== trimmedOption))
    } else {
      onChange([...selectedValues, option])
    }
  }

  const selectAll = () => {
    onChange(filteredOptions)
  }

  const clearAll = () => {
    onChange([])
  }

  const filteredOptions = options.filter((option) => option.toLowerCase().includes(searchText.toLowerCase()))

  const displayText = selectedValues.length === 0 ? 'Select...' : `${selectedValues.length} selected`
  const hasValue = selectedValues.length > 0

  return (
    <MultiSelectContainer ref={dropdownRef}>
      <FieldLabel>{label}</FieldLabel>
      <MultiSelectButton $hasValue={hasValue} onClick={() => setIsOpen(!isOpen)}>
        <span>{displayText}</span>
        <FaChevronDown />
      </MultiSelectButton>
      {isOpen && (
        <MultiSelectPanel>
          <SearchInputWrapper>
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          </SearchInputWrapper>
          <MultiSelectActions>
            <ActionLink onClick={selectAll}>Select All</ActionLink>
            <ActionLink onClick={clearAll}>Clear All</ActionLink>
          </MultiSelectActions>
          <MultiSelectOptions>
            {isLoading ? (
              <LoadingMessage>Loading...</LoadingMessage>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <MultiSelectOption key={option.trim()} $isHighlighted={option === highlightOption}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedValues.map((v) => v.trim()).includes(option.trim())}
                    onChange={() => toggleOption(option)}
                  />
                  <OptionLabel>{option}</OptionLabel>
                </MultiSelectOption>
              ))
            ) : (
              <NoResultsMessage>No results found</NoResultsMessage>
            )}
          </MultiSelectOptions>
        </MultiSelectPanel>
      )}
    </MultiSelectContainer>
  )
}

/**
 * Inline MultiSelect Component for Claim Status (without search)
 */
const ClaimStatusMultiSelect: React.FC<{
  label: string
  options: string[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  isLoading?: boolean
  getOptionLabel?: (option: string) => string
}> = ({ label, options, selectedValues, onChange, isLoading = false, getOptionLabel = (option) => option }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const toggleOption = (option: string) => {
    const trimmedOption = option.trim()
    const trimmedSelectedValues = selectedValues.map((v) => v.trim())

    if (trimmedSelectedValues.includes(trimmedOption)) {
      onChange(selectedValues.filter((v) => v.trim() !== trimmedOption))
    } else {
      onChange([...selectedValues, option])
    }
  }

  const selectAll = () => {
    onChange(options)
  }

  const clearAll = () => {
    onChange([])
  }

  const displayText = selectedValues.length === 0 ? 'Select...' : `${selectedValues.length} selected`
  const hasValue = selectedValues.length > 0

  return (
    <MultiSelectContainer ref={dropdownRef}>
      <FieldLabel>{label}</FieldLabel>
      <MultiSelectButton $hasValue={hasValue} onClick={() => setIsOpen(!isOpen)}>
        <span>{displayText}</span>
        <FaChevronDown />
      </MultiSelectButton>
      {isOpen && (
        <MultiSelectPanel>
          <MultiSelectActions>
            <ActionLink onClick={selectAll}>Select All</ActionLink>
            <ActionLink onClick={clearAll}>Clear All</ActionLink>
          </MultiSelectActions>
          <MultiSelectOptions>
            {isLoading ? (
              <LoadingMessage>Loading...</LoadingMessage>
            ) : (
              options.map((option) => (
                <MultiSelectOption key={option}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedValues.map((v) => v.trim()).includes(option.trim())}
                    onChange={() => toggleOption(option)}
                  />
                  <OptionLabel>{getOptionLabel(option)}</OptionLabel>
                </MultiSelectOption>
              ))
            )}
          </MultiSelectOptions>
        </MultiSelectPanel>
      )}
    </MultiSelectContainer>
  )
}

/**@abstract
 * Appeal Status
 *
 */

/**
 * Owner MultiSelect Component with dynamic data fetching
 */
const OwnerMultiSelect: React.FC<{
  form: FilterFormState
  onUpdateField: <K extends keyof FilterFormState>(key: K, value: FilterFormState[K]) => void
}> = ({ form, onUpdateField }) => {
  const [ownerOptions, setOwnerOptions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentUsername, setCurrentUsername] = useState<string>('')

  useEffect(() => {
    const fetchOwners = async () => {
      setIsLoading(true)
      try {
        const owners = await userNameFilterParams()

        const trimmedOwners = owners ? owners.map((owner: string) => owner.trim()) : []
        const uniqueOwners: string[] = Array.from(new Set(trimmedOwners))

        const currentUser: IUser = storage.get(StorageEnum.currentUser)
        const username = `${currentUser.given_name.trim()} ${currentUser.family_name.trim()}`.trim()
        setCurrentUsername(username)

        const sortedOwners = uniqueOwners.sort((a, b) => {
          if (a === username) return -1
          if (b === username) return 1
          return a.localeCompare(b)
        })

        const fromIndex = sortedOwners.indexOf('Blank')
        sortedOwners.splice(fromIndex, 1)
        sortedOwners.splice(1, 0, 'Blank')

        setOwnerOptions(sortedOwners)
      } catch (error) {
        console.error('Error fetching owners:', error)
        setOwnerOptions([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchOwners()
  }, [])

  return (
    <MultiSelectWithSearch
      label="Owner"
      options={ownerOptions}
      selectedValues={form.owner}
      onChange={(values: string[]) => onUpdateField('owner', values)}
      isLoading={isLoading}
      highlightOption={currentUsername}
    />
  )
}

/**
 * FilterFields Component
 * Renders all filter input fields in a responsive grid layout
 */
export const FilterFields: React.FC<FilterFieldsProps> = ({
  form,
  lastModQuick,
  onUpdateField,
  onLastModQuickChange,
}) => {
  const { data: claimStatusLabels, isLoading: isClaimStatusLoading } = useClaimStatuses()
  useDetailedClaimStatuses()
  const appealEnumDescriptions = useAppealEnumDescriptions()

  const labels = Array.from(new Set((claimStatusLabels ?? []).map((label) => label.trim()).filter(Boolean)))
  if (labels.length > 0 && !labels.includes('Blank')) {
    labels.push('Blank')
  }

  const appealStatusLabels = Object.values<string>(appealEnumDescriptions.appeal_status ?? {}).map(
    formatAppealStatusDescription,
  )
  if (appealStatusLabels.length > 0) {
    appealStatusLabels.push('Blank')
  }
  return (
    <FiltersRow>
      {/* Last Modified Quick Filter */}
      <LastModifiedQuick
        label="Last Modified"
        displayText={lastModQuick.text}
        currentModel={lastModQuick.model}
        onCommit={onLastModQuickChange}
      />

      {/* Date of Service Range */}
      <DateRangeField
        label="Date of Service"
        valueFrom={form.admitDateFrom}
        valueTo={form.admitDateTo}
        operator={form.admitDateOperator}
        onChange={(from, to, operator) => {
          onUpdateField('admitDateFrom', from)
          onUpdateField('admitDateTo', to)
          onUpdateField('admitDateOperator', operator)
        }}
      />

      {/* Extracted Claim Status */}
      <ClaimStatusMultiSelect
        label="Extracted Claim Status"
        options={labels}
        selectedValues={form.claimStatusPayer}
        onChange={(values: string[]) => onUpdateField('claimStatusPayer', values)}
        isLoading={isClaimStatusLoading}
        getOptionLabel={formatClaimStatusLabel}
      />

      {/* Extracted Appeal Status */}
      <ClaimStatusMultiSelect
        label="Extracted Appeal Status"
        options={appealStatusLabels}
        selectedValues={form.appealStatus}
        onChange={(values: string[]) => onUpdateField('appealStatus', values)}
      />

      {/* Batch ID */}
      <FormField>
        <FieldLabel>Batch ID</FieldLabel>
        <FormInput
          $hasValue={!!form.batchCode}
          value={form.batchCode}
          type="number"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdateField('batchCode', e.target.value)}
          placeholder="Enter batch Code"
        />
      </FormField>

      {/* Owner */}
      <OwnerMultiSelect form={form} onUpdateField={onUpdateField} />

      {/* Unique ID */}
      <FormField>
        <FieldLabel>Unique ID</FieldLabel>
        <FormInput
          $hasValue={!!form.uniqueIdentifier}
          value={form.uniqueIdentifier}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdateField('uniqueIdentifier', e.target.value)}
          placeholder="Enter unique ID"
        />
      </FormField>

      {/* Account ID */}
      <FormField>
        <FieldLabel>Account ID</FieldLabel>
        <FormInput
          $hasValue={!!form.visitId}
          value={form.visitId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdateField('visitId', e.target.value)}
          placeholder="Enter Account ID"
        />
      </FormField>

      {/* Member ID */}
      <FormField>
        <FieldLabel>Member ID</FieldLabel>
        <FormInput
          $hasValue={!!form.memberId}
          value={form.memberId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdateField('memberId', e.target.value)}
          placeholder="Enter member ID"
        />
      </FormField>

      {/* Patient ID */}

      {/* Patient Last Name */}
      <FormField>
        <FieldLabel>Patient Last Name</FieldLabel>
        <FormInput
          $hasValue={!!form.patientLastName}
          value={form.patientLastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdateField('patientLastName', e.target.value)}
          placeholder="Enter patient last name"
        />
      </FormField>
    </FiltersRow>
  )
}

/* ---------- Styled Components ---------- */

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.xs};
  width: 100%;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 100px;

  /* Batch ID - compact */
  &:has(input[placeholder*='batch ID']) {
    flex: 1 1 80px;
  }

  /* Owner - medium */
  &:has(input[placeholder*='owner']) {
    flex: 1 1 130px;
  }

  /* Patient Last Name - larger */
  &:has(input[placeholder*='patient last name']) {
    flex: 1.2 1 150px;
    max-width: 200px;
  }

  /* Member ID, Patient ID - compact */
  &:has(input[placeholder*='member ID']),
  &:has(input[placeholder*='patient ID']) {
    flex: 1 1 110px;
    max-width: 200px;
  }

  /* Account ID, Unique ID - compact */
  &:has(input[placeholder*='Account ID']),
  &:has(input[placeholder*='unique ID']) {
    flex: 1 1 110px;
    max-width: 200px;
  }
`

const FieldLabel = styled.label`
  ${({ theme }) => theme.typography.s.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xxxs};
`

const FormInput = styled.input<{ $hasValue?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xxxs};
  height: 32px;
  border: 1px solid ${({ theme, $hasValue }) => ($hasValue ? theme.colors.primary.dark : theme.colors.grey.light)};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  ${({ theme }) => theme.typography.xs.normal};
  background: ${({ theme }) => theme.colors.neutral.lightest};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.dark};
  }
`

const MultiSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1.2 1 170px;
  min-width: 170px;
`

const MultiSelectButton = styled.button<{ $hasValue?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xxxs};
  height: 32px;
  border: 1px solid ${({ theme, $hasValue }) => ($hasValue ? theme.colors.primary.dark : theme.colors.grey.light)};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  ${({ theme }) => theme.typography.xs.normal};
  color: ${({ theme }) => theme.colors.grey.darker};
  background: ${({ theme }) => theme.colors.neutral.lightest};
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.grey.neutral};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.neutral};
  }

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.grey.darker};
  }

  svg {
    margin-left: auto;
    font-size: 12px;
    opacity: 0.8;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.grey.darker};
  }
`

const MultiSelectPanel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.neutral.lightest};
  border: 1px solid ${({ theme }) => theme.colors.grey.light};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  display: flex;
  width: 210px;
  flex-direction: column;
`

const MultiSelectActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.light};
  background: ${({ theme }) => theme.colors.grey.lightest};
  justify-content: space-between;
`

const ActionLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary.neutral};
  ${({ theme }) => theme.typography.s.medium};
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`

const MultiSelectOptions = styled.div`
  overflow-y: auto;
  max-height: 250px;
`

const MultiSelectOption = styled.label<{ $isHighlighted?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  ${({ theme }) => theme.typography.s.normal};
  background: ${({ theme, $isHighlighted }) => ($isHighlighted ? theme.colors.primary.lightest : 'transparent')};
  color: ${({ theme, $isHighlighted }) => ($isHighlighted ? theme.colors.primary.dark : theme.colors.grey.darker)};
  font-weight: ${({ $isHighlighted }) => ($isHighlighted ? '600' : '400')};

  &:hover {
    background: ${({ theme, $isHighlighted }) =>
      $isHighlighted ? theme.colors.primary.lighter : theme.colors.grey.lightest};
  }
`

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  margin-right: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: ${({ theme }) => theme.colors.primary.dark};

  &:hover {
    accent-color: ${({ theme }) => theme.colors.primary.dark};
  }

  &:focus {
    outline-color: ${({ theme }) => theme.colors.primary.dark};
  }
`

const OptionLabel = styled.span`
  flex: 1;
  word-break: break-word;
  white-space: normal;
`

const SearchInputWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.light};
`

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.colors.grey.light};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  ${({ theme }) => theme.typography.xs.normal};
  background: ${({ theme }) => theme.colors.neutral.lightest};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.dark};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey.neutral};
  }
`

const LoadingMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.m};
  text-align: center;
  color: ${({ theme }) => theme.colors.grey.neutral};
  ${({ theme }) => theme.typography.s.normal};
`

const NoResultsMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.m};
  text-align: center;
  color: ${({ theme }) => theme.colors.grey.neutral};
  ${({ theme }) => theme.typography.s.normal};
`
